import Image from "next/image";
import clsx from "clsx";

import { GetLeaderboardRankingsQuery } from "@/graphql/types";
import { NO_IMAGE_URL } from "@/utils/constants";
import { displayEntryScore } from "@/components/LeaderboardCard/helpers";
import ConditionalLink from "@/components/hoc/ConditionalLink";
import { getUserUrl } from "@/utils/getUserInfo";

interface LeaderboardPodiumProps {
  entries: GetLeaderboardRankingsQuery["leaderboardRankings"];
}

function LeaderboardPodium({ entries }: LeaderboardPodiumProps) {
  const [first, second, third] = entries;

  return (
    <div
      className={clsx(
        "grid grid-cols-[1fr,auto,1fr] gap-5 lg:gap-6 bg-dark-card rounded-lg pb-5 lg:pb-6",
        "text-xs leading-1.25 lg:text-sm lg:leading-1.25 xl:text-base xl:leading-1.25 relative",
        "before:bg-dark-6/10 before:bg-dark-stroke before:border before:border-gradient before:rounded-lg"
      )}
    >
      <div className="-mt-3 lg:-mt-5 xl:-mt-7 w-full flex justify-end min-w-0 z-10">
        <ConditionalLink
          href={second ? getUserUrl(second.user) : undefined}
          className="flex flex-col items-end"
        >
          <div className="w-14 h-14 lg:w-18 lg:h-18 xl:w-22 xl:h-22 shrink-0 relative">
            <div
              className={clsx(
                "absolute left-1/2 bottom-0 -translate-x-1/2 translate-y-[40%] z-10",
                "bg-gradient-to-br from-blue-5 to-blue-3 rounded-full shadow-medium",
                "flex items-center justify-center w-7 lg:w-8 h-5 lg:h-6",
                "text-white font-medium text-xs leading-1 lg:text-sm lg:leading-1.1"
              )}
            >
              2
            </div>
            <Image
              src={second?.user?.avatarUrl ?? NO_IMAGE_URL}
              alt=""
              layout="fill"
              sizes="96px"
              className="rounded-full"
            />
          </div>
          <div className="pr-1 mt-4.5 xl:mt-5.5 truncate font-medium text-right max-w-full">
            {second?.user.displayName ?? "--"}
          </div>
          <span className="pr-1 mt-0.5 xl:mt-1 text-purple font-medium">{displayEntryScore(second)}</span>
        </ConditionalLink>
      </div>

      <ConditionalLink href={first ? getUserUrl(first.user) : undefined}>
        <div
          className={
            "-mt-6 lg:-mt-8 xl:-mt-10 flex flex-col items-center min-w-0 max-w-[clamp(100px,10vw,200px)] z-10"
          }
        >
          <div
            className={clsx(
              "w-14 h-14 lg:w-20 lg:h-20 xl:w-24 xl:h-24 shrink-0 relative rounded-full",
              "bg-gradient-to-tr from-purple via-purple-3 to-purple z-10"
            )}
          >
            <div className="absolute inset-0.5">
              <div
                className={clsx(
                  "absolute left-1/2 bottom-0 -translate-x-1/2 translate-y-[40%] z-10",
                  "bg-gradient-to-br from-purple-3 to-purple rounded-full shadow-medium",
                  "flex items-center justify-center w-8 lg:w-10 h-6 lg:h-7",
                  "text-white font-medium text-xs leading-1 lg:text-sm lg:leading-1.1"
                )}
              >
                1
              </div>
              <Image
                src={first?.user?.avatarUrl ?? NO_IMAGE_URL}
                alt=""
                layout="fill"
                sizes="96px"
                className="rounded-full bg-purple-4"
              />
            </div>
          </div>
          <div className="mt-4.5 xl:mt-5.5 truncate font-medium text-center max-w-full">
            {first?.user.displayName ?? "--"}
          </div>
          <span className="mt-0.5 xl:mt-1 text-purple font-medium">{displayEntryScore(first)}</span>
        </div>
      </ConditionalLink>

      <div className="-mt-3 lg:-mt-5 xl:-mt-7 w-full flex min-w-0 z-10">
        <ConditionalLink
          href={third ? getUserUrl(third.user) : undefined}
          className="flex flex-col items-start"
        >
          <div className="w-14 h-14 lg:w-18 lg:h-18 xl:w-22 xl:h-22 shrink-0 relative">
            <div
              className={clsx(
                "absolute left-1/2 bottom-0 -translate-x-1/2 translate-y-[40%] z-10",
                "bg-gradient-to-br from-green-4 to-green-3 rounded-full shadow-medium",
                "flex items-center justify-center w-7 lg:w-8 h-5 lg:h-6",
                "text-white font-medium text-xs leading-1 lg:text-sm lg:leading-1.1"
              )}
            >
              3
            </div>
            <Image
              src={third?.user?.avatarUrl ?? NO_IMAGE_URL}
              alt=""
              layout="fill"
              sizes="96px"
              className="rounded-full"
            />
          </div>
          <div className="pl-1 mt-4.5 xl:mt-5.5 truncate font-medium text-left max-w-full">
            {third?.user.displayName ?? "--"}
          </div>
          <span className="pl-1 mt-0.5 xl:mt-1 text-purple font-medium">{displayEntryScore(third)}</span>
        </ConditionalLink>
      </div>
    </div>
  );
}

export default LeaderboardPodium;
